import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';

import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import {
  getAllModulesInitActions,
  getCustomInitActionsForWidgets,
  getModuleData,
  getPageInfoInitActions,
  getRawSectionModules,
  getSectionModules,
} from './common';
import { withOffset } from '@wix/da-shared-react/pkg/Stream';
import { ALL_FOLDER_ID, SCRAPS_FOLDER_ID } from '@wix/da-url';
import { EmptyCollectionsFolderException } from '../../utils/exceptions';
import {
  getFolderDeviationsStreamId,
  getFolderDeviationsStreamParams,
  FOLDER_DEVIATIONS_STREAM_GROUP_ID,
  DEVIATIONS_PER_FETCH_COUNT,
} from '../../streams/folderDeviations';
import { ModuleType } from '../../../types/modules';
import { getFoldersModuleInitAction } from './modules';
import {
  setSelectedFolderId,
  setGallectionSectionType,
  setFullViewFolderIds,
  setSelectedSubfolderId,
} from '../../actions/gallectionSection';
import { setTotalPages } from '../../actions/pageInfo';
import { getDefaultFolderFromFolders } from '../../components/Sections/GallectionsSection/helpers';
import { PapiGallection, PapiUser } from '@wix/da-papi-types';
import { getGallectionTypeFromSection } from '../../utils/gallection';
import { PublicSessionState } from '@wix/da-react-context/pkg/SessionContext';
import { getGallectionWidgetConfigs } from '../../components/Sections/GallectionsSection/gallectionWidgetConfigs';

export function getFolderDeviationsInitAction(
  gallectionType,
  responseData,
  requestParams,
  session: PublicSessionState
) {
  const moduleData = getModuleData(
    getRawSectionModules(responseData),
    ModuleType.FOLDER_DEVIATIONS
  );
  if (!moduleData) {
    return [];
  }
  const pageSize = session.pageSize ?? 0;
  const isPaginated = session.pagingMode === 'page';

  const { result, entities } = normalize(moduleData.deviations, [
    deviationSchema,
  ]);

  return [
    withOffset.actions.initialize({
      streamId: getFolderDeviationsStreamId(
        gallectionType,
        moduleData.folderId
      ),
      streamParams: getFolderDeviationsStreamParams(
        gallectionType,
        moduleData.folderId
      ),
      groupId: FOLDER_DEVIATIONS_STREAM_GROUP_ID,
      itemsPerFetch: isPaginated ? pageSize : DEVIATIONS_PER_FETCH_COUNT,
      nextOffset: moduleData.nextOffset,
      items: result,
      hasMore: !result || !result.length ? false : moduleData.hasMore,
      entities,
    }),
    setTotalPages(moduleData.totalPageCount || 1),
  ];
}

export function getGallectionsSectionInitActions(
  section: 'gallery' | 'favourites' | 'private_collections',
  responseData,
  requestParams,
  session: PublicSessionState
) {
  const gallectionType = getGallectionTypeFromSection(section);

  const foldersModuleData = getModuleData(
    getRawSectionModules(responseData) ?? [],
    ModuleType.FOLDERS
  );

  const folders: PapiGallection[] =
    foldersModuleData && foldersModuleData.results;
  if (!folders) {
    return [];
  }

  let initFolderId;
  let initSubfolderId;
  if (requestParams.all_folder) {
    initFolderId = ALL_FOLDER_ID;
  } else if (requestParams.scraps_folder) {
    initFolderId = SCRAPS_FOLDER_ID;
  } else if (requestParams.folder_id) {
    initFolderId = +requestParams.folder_id;
    const initFolder = folders
      .flatMap(folder => [folder, ...(folder.subfolders ?? [])])
      .find(folder => folder.folderId === initFolderId);

    // If the requested folder is a collection (favourites) and is empty we want to return a 404
    if (!initFolder || (initFolder.size < 1 && section === 'favourites')) {
      const username = session.user && (session.user as PapiUser).username;
      throw new EmptyCollectionsFolderException(`${username}`);
    } else if (initFolder.parentId) {
      initFolderId = initFolder.parentId;
      initSubfolderId = initFolder.folderId;
    }
  } else {
    initFolderId = getDefaultFolderFromFolders(folders)?.folderId;
  }
  const commentOrder =
    session.user && session.commentsOrder ? session.commentsOrder : 'newest';
  const params = {
    commentOrder,
  };

  return [
    setGallectionSectionType(gallectionType),
    setSelectedFolderId(initFolderId),
    setSelectedSubfolderId(initSubfolderId),
    setFullViewFolderIds([initFolderId]),
    getFoldersModuleInitAction(getSectionModules(responseData), {
      gallectionType,
    }),
    ...getFolderDeviationsInitAction(
      gallectionType,
      responseData,
      requestParams,
      session
    ),
    ...getAllModulesInitActions(responseData, params),
    ...getPageInfoInitActions(responseData, requestParams),
  ];
}

// new grusers
export const getCustomInitActionsForGallectionWidgets = (
  section: 'gallery' | 'favourites' | 'private_collections',
  responseData,
  requestParams
) => [
  ...getCustomInitActionsForWidgets(
    responseData,
    requestParams,
    getGallectionWidgetConfigs({ t: () => null, dispatch: () => null })
  ),
  setGallectionSectionType(getGallectionTypeFromSection(section)),
  ...getPageInfoInitActions(responseData, requestParams),
];
